import { makeAutoObservable } from 'mobx';
import companyViewModel from './company-view-model';
import { CompanyAddFormData } from '../../../model/professional/compagny-model';
import compagnyModel from '../../../model/professional/compagny-model';

export class CompanyAddFormViewModel {

  // État du formulaire
  formData: CompanyAddFormData = {
    businessName: '',
    description: '',
    domain: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    siren: '',
    sirenVerified: false,
    reservationConditions: '',
    paymentMethods: [],
    profilePicture: null,
    galleryPhoto: [],
    logo: null
  };

  galleryPhotoFiles: File[] = [];

  galleryPhotoPreviewUrls: string[] = [];

  logoPreviewUrl: string | null = null;

  errors: Partial<Record<keyof CompanyAddFormData, string>> = {};

  successMessages: Partial<Record<keyof CompanyAddFormData, string>> = {};

  isVerifyingSiren: boolean = false;

  private businessNameCheckTimer: NodeJS.Timeout | null = null;

  private static instance: CompanyAddFormViewModel;

  private constructor() {
    makeAutoObservable(this);
  }

  public static getInstance(): CompanyAddFormViewModel {
    if (!CompanyAddFormViewModel.instance) {
      CompanyAddFormViewModel.instance = new CompanyAddFormViewModel();
    }
    return CompanyAddFormViewModel.instance;
  }

  setFormData(data: Partial<CompanyAddFormData>) {
    this.formData = { ...this.formData, ...data };
  }

  setErrors(errors: Partial<Record<keyof CompanyAddFormData, string>>) {
    this.errors = errors;
  }

  setError(field: keyof CompanyAddFormData, message: string) {
    this.errors = { ...this.errors, [field]: message };
  }

  clearError(field: keyof CompanyAddFormData) {
    const newErrors = { ...this.errors };
    delete newErrors[field];
    this.errors = newErrors;
  }

  setSuccessMessage(field: keyof CompanyAddFormData, message: string) {
    this.successMessages = { ...this.successMessages, [field]: message };
  }

  clearSuccessMessage(field: keyof CompanyAddFormData) {
    const newSuccessMessages = { ...this.successMessages };
    delete newSuccessMessages[field];
    this.successMessages = newSuccessMessages;
  }

  setIsVerifyingSiren(verifying: boolean) {
    this.isVerifyingSiren = verifying;
  }

  cleanup() {
    this.galleryPhotoPreviewUrls.forEach(url => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });

    if (this.logoPreviewUrl && this.logoPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.logoPreviewUrl);
    }
  }

  resetForm() {
    this.cleanup();

    this.formData = {
      businessName: '',
      description: '',
      domain: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      postalCode: '',
      country: '',
      siren: '',
      sirenVerified: false,
      reservationConditions: '',
      paymentMethods: [],
      profilePicture: null,
      galleryPhoto: [],
      logo: null
    };
    this.errors = {};
    this.galleryPhotoFiles = [];
    this.galleryPhotoPreviewUrls = [];
    this.logoPreviewUrl = null;
  }

  handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    this.setFormData({ [name]: value });

    if (this.errors[name as keyof CompanyAddFormData]) {
      this.clearError(name as keyof CompanyAddFormData);
    }
    if (this.successMessages[name as keyof CompanyAddFormData]) {
      this.clearSuccessMessage(name as keyof CompanyAddFormData);
    }

    if (name === 'businessName' && value.trim().length > 2) {
      this.checkBusinessNameAvailabilityDebounced(value.trim());
    }
  };

  checkBusinessNameAvailabilityDebounced = (businessName: string) => {
    if (this.businessNameCheckTimer) {
      clearTimeout(this.businessNameCheckTimer);
    }

    this.businessNameCheckTimer = setTimeout(async () => {
      try {
        const result = await compagnyModel.checkBusinessNameAvailability(businessName);

        if (!result.available) {
          this.setError('businessName', result.message);
          this.clearSuccessMessage('businessName');
        } else {
          this.clearError('businessName');
          this.setSuccessMessage('businessName', result.message);
        }
      } catch (error) {
        console.error('Error checking business name availability:', error);
      }
    }, 500);
  };

  handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.size > 2 * 1024 * 1024) {
        this.setError('logo', 'Le logo ne doit pas dépasser 2MB');
        return;
      }

      if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
        this.setError('logo', 'Le logo doit être au format JPG, PNG ou WEBP');
        return;
      }

      this.setFormData({ logo: file });

      if (this.logoPreviewUrl) {
        URL.revokeObjectURL(this.logoPreviewUrl);
      }
      this.logoPreviewUrl = URL.createObjectURL(file);
      this.clearError('logo');
    }
  };

  removeLogo = () => {
    if (this.logoPreviewUrl) {
      URL.revokeObjectURL(this.logoPreviewUrl);
      this.logoPreviewUrl = null;
    }
    this.setFormData({ logo: null });
  };

  handleGalleryPhotosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      const maxPhotos = 5;

      if (this.galleryPhotoFiles.length + files.length > maxPhotos) {
        this.setError('galleryPhoto', `Vous ne pouvez ajouter que ${maxPhotos} photos maximum`);
        return;
      }

      const validFiles: File[] = [];
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          this.setError('galleryPhoto', 'Chaque photo ne doit pas dépasser 5MB');
          continue;
        }

        if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
          this.setError('galleryPhoto', 'Les photos doivent être au format JPG, PNG ou WEBP');
          continue;
        }

        validFiles.push(file);
      }

      if (validFiles.length > 0) {
        this.galleryPhotoFiles = [...this.galleryPhotoFiles, ...validFiles];

        validFiles.forEach(file => {
          this.galleryPhotoPreviewUrls.push(URL.createObjectURL(file));
        });

        this.clearError('galleryPhoto');
      }
    }
  };

  removeGalleryPhoto = (index: number) => {
    if (this.galleryPhotoPreviewUrls[index]) {
      URL.revokeObjectURL(this.galleryPhotoPreviewUrls[index]);
    }

    this.galleryPhotoFiles = this.galleryPhotoFiles.filter((_, i) => i !== index);
    this.galleryPhotoPreviewUrls = this.galleryPhotoPreviewUrls.filter((_, i) => i !== index);
  };

  verifySiren = async (t: any) => {
    if (!this.formData.siren || this.formData.siren.length !== 9) {
      this.setError('siren', t('company.form.errors.sirenInvalid', 'Le SIREN doit contenir 9 chiffres') as string);
      return;
    }

    try {
      this.setIsVerifyingSiren(true);

      const result = await compagnyModel.verifySiren(this.formData.siren);

      if (result.success && result.isValid) {
        this.setFormData({ sirenVerified: true });
        this.clearError('siren');
      } else {
        this.setError('siren', result.message || 'SIREN invalide');
      }

      this.setIsVerifyingSiren(false);
    } catch (error) {
      console.error('Erreur lors de la vérification du SIREN:', error);
      this.setError('siren', 'Erreur lors de la vérification du SIREN');
      this.setIsVerifyingSiren(false);
    }
  };

  validateForm = (t: any): boolean => {
    const newErrors: Partial<Record<keyof CompanyAddFormData, string>> = {};

    if (!this.formData.businessName.trim()) {
      newErrors.businessName = t('company.form.errors.businessNameRequired', 'Le nom de l\'entreprise est requis') as string;
    }

    if (!this.formData.domain.trim()) {
      newErrors.domain = t('company.form.errors.domainRequired', 'Le domaine d\'activité est requis') as string;
    }

    if (!this.formData.address.trim()) {
      newErrors.address = t('company.form.errors.addressRequired', 'L\'adresse est requise') as string;
    }

    if (!this.formData.city.trim()) {
      newErrors.city = t('company.form.errors.cityRequired', 'La ville est requise') as string;
    }

    if (!this.formData.postalCode.trim()) {
      newErrors.postalCode = t('company.form.errors.postalCodeRequired', 'Le code postal est requis') as string;
    }

    if (!this.formData.country.trim()) {
      newErrors.country = t('company.form.errors.countryRequired', 'Le pays est requis') as string;
    }

    if (!this.formData.siren.trim()) {
      newErrors.siren = t('company.form.errors.sirenRequired', 'Le numéro SIREN est requis') as string;
    } else if (!/^\d{9}$/.test(this.formData.siren)) {
      newErrors.siren = t('company.form.errors.sirenInvalid', 'Le SIREN doit contenir 9 chiffres') as string;
    }

    this.setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  handleSubmit = async (e: React.FormEvent, t: any) => {
    e.preventDefault();

    if (this.validateForm(t)) {
      const submissionData = {
        ...this.formData,
        galleryPhoto: this.galleryPhotoFiles
      };
      await companyViewModel.handleNewCompany(submissionData);
      // Réinitialiser le formulaire après succès si nécessaire
      // this.resetForm();
    }
  };

  handlePaymentMethodChange = (method: string, checked: boolean) => {
    const updatedMethods = checked
      ? [...this.formData.paymentMethods, method]
      : this.formData.paymentMethods.filter(m => m !== method);

    this.setFormData({ paymentMethods: updatedMethods });
  };
}

export default CompanyAddFormViewModel.getInstance();
